import { Link } from 'react-router-dom';
import { FileCheck, FileX, AlertCircle, Home, Briefcase, ChevronRight, Shield, Clock, CreditCard, Star, FileText } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CATEGORIES } from '../data/documents';

const iconMap = {
  FileCheck: FileCheck,
  FileX: FileX,
  AlertCircle: AlertCircle,
  Home: Home,
  Briefcase: Briefcase,
};

const CATEGORY_ICONS_BIG = {
  attestations: '📋',
  resiliation: '✂️',
  reclamations: '⚖️',
  logement: '🏠',
  travail: '💼',
};

const POPULAR_DOCS = [
  { id: 'attestation-hebergement', label: 'Attestation d\'hébergement', emoji: '🏠', desc: 'Pour la préfecture, la CAF, les impôts' },
  { id: 'resiliation-assurance',   label: 'Résiliation assurance',      emoji: '✂️', desc: 'Auto, habitation, complémentaire santé' },
  { id: 'lettre-reclamation',      label: 'Lettre de réclamation',      emoji: '⚖️', desc: 'SAV, remboursement, litige consommateur' },
  { id: 'preavis-logement',        label: 'Préavis de départ',          emoji: '📬', desc: 'Location meublée ou vide, 1 ou 3 mois' },
  { id: 'lettre-employeur',        label: 'Lettre à l\'employeur',      emoji: '💼', desc: 'Démission, congé, absence justifiée' },
  { id: 'attestation-honneur',     label: 'Attestation sur l\'honneur', emoji: '📋', desc: 'Déclaration personnelle certifiée' },
];

const REVIEWS = [
  {
    name: 'Marie L.',
    note: 5,
    text: 'Très simple d\'utilisation. Mon attestation d\'hébergement était prête en quelques minutes, j\'ai pu l\'imprimer directement pour la préfecture.',
  },
  {
    name: 'Julien R.',
    note: 4,
    text: 'Bon service, le formulaire est guidé et le PDF rendu proprement. J\'aurais aimé pouvoir modifier après coup sans repayer, mais pour un usage ponctuel c\'est parfait.',
  },
  {
    name: 'Sophie M.',
    note: 5,
    text: 'J\'avais besoin d\'une lettre de résiliation en urgence un dimanche soir. Disponible immédiatement, document conforme. Exactement ce dont j\'avais besoin.',
  },
];

const FAQ_ITEMS = [
  {
    q: 'Les documents sont-ils juridiquement valables ?',
    a: 'Nos modèles sont conformes aux usages administratifs français. Ils peuvent être utilisés pour vos démarches courantes. L\'utilisateur reste responsable des informations qu\'il fournit.',
  },
  {
    q: 'Puis-je voir le document avant de payer ?',
    a: 'Oui, un aperçu partiel est disponible gratuitement pour vérifier la mise en forme et vos informations. Vous ne payez que si vous souhaitez télécharger le PDF final complet.',
  },
  {
    q: 'Comment fonctionne le paiement ?',
    a: 'Le paiement est sécurisé par Stripe. Vous payez 4,99 € par document, sans abonnement ni frais cachés. Le téléchargement est immédiat après paiement.',
  },
  {
    q: 'Mes données sont-elles protégées ?',
    a: 'Vos informations sont utilisées uniquement pour générer votre document. Nous ne conservons pas vos données personnelles au-delà du temps nécessaire au traitement.',
  },
];

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>Générateur de documents PDF administratifs | PDF Facile</title>
        <meta name="description" content="Générez facilement vos documents administratifs en PDF : attestations, lettres de résiliation et démarches courantes. Aperçu gratuit, téléchargement immédiat à 4,99 €." />
        <link rel="canonical" href="https://pdf-facile.fr/" />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-white">
        <Header />

        <main className="flex-1">

          {/* ── Hero ── */}
          <section className="bg-gradient-to-b from-slate-50 to-white pt-16 pb-12 px-4">
            <div className="container-app text-center">

              {/* Badge social proof */}
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-sm text-slate-600 shadow-sm mb-6">
                <span className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                </span>
                <span className="font-medium">+2 400 documents générés</span>
                <span className="text-slate-400">·</span>
                <span>Note 4,9/5</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4 text-balance leading-tight" data-testid="hero-title">
                Votre document officiel<br className="hidden sm:block" /> prêt en 2 minutes
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
                Attestations, résiliations, réclamations — conformes aux usages administratifs français.
                Aperçu gratuit, PDF téléchargeable à <strong className="text-slate-700">4,99 €</strong>.
              </p>

              {/* CTA principal */}
              <a
                href="#categories"
                className="inline-flex items-center gap-2 bg-primary text-white font-semibold text-base px-7 py-3.5 rounded-lg shadow hover:bg-primary/90 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Choisir mon document
              </a>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm text-slate-500 mt-8">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Documents conformes</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span>Prêt en 2 minutes</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-purple-500" />
                  <span>Paiement sécurisé Stripe</span>
                </div>
              </div>
            </div>
          </section>

          {/* ── Categories ── */}
          <section className="section-spacing" id="categories">
            <div className="container-app">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2" data-testid="categories-title">
                Choisissez votre type de document
              </h2>
              <p className="text-center text-muted-foreground mb-8">24 modèles disponibles, conformes et prêts à l'emploi</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {CATEGORIES.map((category) => {
                  const IconComponent = iconMap[category.icon] || FileCheck;
                  const emoji = CATEGORY_ICONS_BIG[category.id] || '📄';
                  return (
                    <Link
                      key={category.id}
                      to={`/categorie/${category.id}`}
                      className="group flex gap-4 bg-white border border-slate-200 rounded-xl p-5 hover:border-primary hover:shadow-md transition-all"
                      data-testid={`category-card-${category.id}`}
                    >
                      <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-2xl shrink-0 group-hover:bg-primary/10 transition-colors">
                        {emoji}
                      </div>
                      <div className="flex flex-col flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-primary transition-colors">
                          {category.name}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                          {category.description}
                        </p>
                        <div className="flex items-center gap-1 mt-3 text-primary font-medium text-sm">
                          <span>Voir les modèles</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── Documents populaires ── */}
          <section className="section-spacing bg-slate-50">
            <div className="container-app">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2">
                Documents les plus demandés
              </h2>
              <p className="text-center text-muted-foreground mb-8">Accédez directement aux modèles les plus utilisés</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {POPULAR_DOCS.map(({ id, label, emoji, desc }) => (
                  <Link
                    key={id}
                    to={`/document/${id}`}
                    className="group flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-primary hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-xl shrink-0 group-hover:bg-primary/10 transition-colors">
                      {emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 group-hover:text-primary transition-colors">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* ── How it works ── */}
          <section className="section-spacing">
            <div className="container-app">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
                Comment ça marche ?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { n: 1, title: 'Choisissez un document', desc: 'Sélectionnez le type de document parmi nos 24 modèles couvrant toutes vos démarches.' },
                  { n: 2, title: 'Remplissez le formulaire', desc: 'Complétez vos informations et prévisualisez l\'aperçu partiel gratuitement avant de payer.' },
                  { n: 3, title: 'Téléchargez votre PDF', desc: 'Payez 4,99 € par carte bancaire et téléchargez immédiatement votre document prêt à signer.' },
                ].map(({ n, title, desc }) => (
                  <div key={n} className="text-center">
                    <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">{n}</div>
                    <h3 className="font-semibold text-lg mb-2">{title}</h3>
                    <p className="text-muted-foreground text-sm">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Social Proof / Avis ── */}
          <section className="section-spacing bg-slate-50">
            <div className="container-app">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">Ce qu'en disent nos utilisateurs</h2>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                  <span className="flex">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                  </span>
                  <span className="font-medium text-slate-700">4,9/5</span>
                  <span>· +2 400 documents générés</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {REVIEWS.map((r, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary text-sm">
                        {r.name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900">{r.name}</p>
                        <span className="flex">
                          {[...Array(r.note)].map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                          {[...Array(5 - r.note)].map((_, j) => <Star key={`e-${j}`} className="w-3 h-3 text-slate-200" />)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">"{r.text}"</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="section-spacing">
            <div className="container-app max-w-3xl">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8">Questions fréquentes</h2>
              <div className="space-y-5">
                {FAQ_ITEMS.map(({ q, a }, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                    <h3 className="font-semibold text-slate-900 mb-2">{q}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── SEO Content ── */}
          <section className="bg-white py-8 border-t border-slate-100">
            <div className="container-app max-w-3xl">
              <p className="text-muted-foreground text-sm leading-relaxed">
                <strong>PDF Facile</strong> est un générateur de documents PDF administratifs simple et rapide.
                Créez en quelques clics vos attestations sur l'honneur, attestations d'hébergement,
                lettres de résiliation ou tout autre document administratif courant. Aucune inscription n'est
                nécessaire : remplissez le formulaire, visualisez l'aperçu gratuitement, puis téléchargez
                votre PDF après paiement. Nos modèles sont conformes aux usages administratifs français
                et prêts à l'emploi pour vos démarches.
              </p>
            </div>
          </section>

        </main>

        <Footer />
      </div>
    </>
  );
}
